"use client";

import { Hand, Leaf, Waves, Wind } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const principles = [{
  icon: Leaf,
  number: "01 · SCOPE",
  title: "Bathroom Remodel Scope",
  description: "Bathroom remodel projects can include tile, flooring, vanities, fixtures, showers, tubs, lighting, storage, and layout updates."
}, {
  icon: Wind,
  number: "02 · LAYOUT",
  title: "Design and Layout Options",
  description: "Bathroom layout updates can help make the space more useful, comfortable, and easier to use for daily routines."
}, {
  icon: Hand,
  number: "03 · QUOTE",
  title: "Quote Requests",
  description: "Share your project details to request a bathroom remodel quote for Lakeland, FL and nearby Polk County areas."
}, {
  icon: Waves,
  number: "04 · FUNCTION",
  title: "Comfort and Function",
  description: "A bathroom remodel can focus on daily comfort, storage, accessibility, fixtures, shower updates, tub updates, and the overall look of the space."
}];
function PrincipleColumn({
  principle,
  index
}: {
  principle: (typeof principles)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const Icon = principle.icon;
  return <div ref={ref} data-index={index} className="relative px-6 py-10 md:px-8 md:py-0 transition-all duration-700 ease-out" style={{
    transitionDelay: `${index * 120}ms`,
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)"
  }}>
      <Icon className="size-12 mb-8 stroke-[1] text-secondary" aria-hidden="true" />

      <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground mb-5 font-default">
        {principle.number}
      </div>

      <h3 className="font-serif font-default text-[26px] leading-tight text-foreground mb-5">
        {principle.title}
      </h3>

      <p className="text-[15px] leading-relaxed text-muted-foreground font-default">
        {principle.description}
      </p>
    </div>;
}
export default function RemodelPrinciples() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2744" id="bathroom-remodel-services-in-lakeland" className="bg-background text-foreground py-24 md:py-32 lg:py-40 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div ref={headerRef} className="text-center max-w-4xl mx-auto mb-20 md:mb-28 transition-all duration-700 ease-out" style={{
        opacity: headerVisible ? 1 : 0,
        transform: headerVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          <div className="text-[11px] tracking-[0.22em] uppercase text-foreground/70 mb-8 font-default">
            REMODEL · PLANNING · 04
          </div>

          <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-[64px] leading-[1.05] text-foreground">
            Bathroom Remodeling Services in Lakeland, FL.
          </h2>

          <p className="mt-6 mx-auto max-w-2xl text-base md:text-lg leading-relaxed text-muted-foreground font-default">
            Bathroom remodeling in Lakeland, FL can include shower remodels, tub
            replacements, tile, flooring, vanities, fixtures, lighting, storage,
            and full bathroom renovation options.
          </p>

          <p className="mt-5 mx-auto max-w-2xl text-base leading-relaxed text-muted-foreground font-default">
            For larger projects, view our{" "}
            <Link to="/bathroom-remodel/" className="text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
              bathroom remodel services in Lakeland, FL
            </Link>
            .
          </p>
        </div>

        <div className="bg-card rounded-lg shadow-md">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-border/60">
            {principles.map((principle, i) => <div key={principle.title} data-index={i} className={i >= 2 ? "sm:border-t sm:border-border/60 lg:border-t-0" : ""}>
                <PrincipleColumn principle={principle} index={i} />
              </div>)}
          </div>
        </div>
      </div>

      <div ref={imageRef} className="mt-20 md:mt-28 lg:mt-32 transition-all duration-1000 ease-out" style={{
      opacity: imageVisible ? 1 : 0,
      transform: imageVisible ? "translateY(0)" : "translateY(32px)"
    }}>
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709164/img/bathroom_remodel_lakeland.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709164/img/bathroom_remodel_lakeland.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709164/img/bathroom_remodel_lakeland.png-wvc-sizes" alt="Bathroom remodel in Lakeland FL with tile and fixtures" loading="lazy" decoding="async" className="w-full h-[50vh] md:h-[65vh] lg:h-[75vh] object-cover" />

        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-6">
          <p className="font-serif italic text-sm md:text-base text-foreground/70 font-default">
            Bathroom remodel ideas and quote requests for homeowners in
            Lakeland, FL and nearby Polk County areas.
          </p>
        </div>
      </div>
    </section>;
}